/**
 * The project overview — the architect's morning glance (`chat1.md` wireframe 6, C1).
 *
 * **Every number on this screen is a number the product can actually stand behind.** The
 * design's metric row has four cards and one of them counts overdue *tasks*, which do not
 * exist yet: `MCK-20260521-008` is a written spec with no domain behind it. A dashboard that
 * printed `0` there would be a lie in the one place people look for a summary, so the card is
 * absent and this catalogue has no string for it.
 *
 * `metric.openReviews.more` exists because a count over a page is a count with a floor rather
 * than a value. Saying `50+` is honest; saying `50` is not.
 */

export const overviewEn = {
  'page.for': 'for {client}',
  'page.phase': 'Phase: {phase}',

  'metric.awaitingYou': 'Awaiting you',
  'metric.openReviews': 'Open reviews',
  'metric.publishedThisWeek': 'Published this week',
  'metric.drawings': 'Drawings',
  'metric.more': '{count}+',

  'attention.title': 'Needs your attention',
  'attention.empty': 'Nothing is waiting on you.',
  'attention.reviewDue': 'A review is due {when}',
  'attention.reviewOverdue': 'A review is overdue',
  'attention.open': 'Open it',

  'recent.title': 'Recently published',
  'recent.empty': 'Nothing has been published yet.',

  'activity.title': 'Activity',
  'activity.empty': 'Nothing has been recorded yet.',
  'activity.all': 'Show the whole log',

  'people.title': 'People',
  'people.more': '{count} more',
  'people.empty': 'Nobody has been added to this project yet.',

  // The empty project. Three steps in the order they have to happen — a tree to file drawings
  // in, people to send them to, and then the first sheet.
  'start.title': 'Get started',
  'start.body': 'This project is empty. Three things make it useful:',
  'start.structure': 'Set up the structure',
  'start.people': 'Invite your team',
  'start.publish': 'Publish the first version',

  'status.loading': 'Loading the project…',

  // Said once, at the bottom, rather than as a blank card in the metric row: the honest
  // version of "we have not built this yet".
  'missing.tasks': 'Tasks are not built yet, so nothing here counts them.',
} as const;

export type OverviewMessageKey = keyof typeof overviewEn;
